import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Space,
  Table,
  Modal,
  Upload,
  Typography,
  Form,
  Input,
  InputNumber,
  Tooltip,
  Input as AntInput,
  message,
  Popconfirm,
  Collapse,
  List,
  Alert,
  Tag,
  Checkbox,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  InfoCircleOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import { useCompany } from "../hooks/useCompany";
import { useCompanyDetails } from "../hooks/useCompanyDetails";
import {
  useCompanyProducts,
  CompanyProduct,
} from "../hooks/useCompanyProducts";
import type { ColumnsType } from "antd/es/table";
import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { useProductBookings } from "../hooks/useProductBookings";
import { PicqerService } from "../services/picqerService";

const { Search } = AntInput;
const { Panel } = Collapse;

// Add a CSS class for compact tables
const compactTableStyles = css`
  .ant-table-thead > tr > th {
    padding: 8px 16px;
  }
  .ant-table-tbody > tr > td {
    padding: 6px 16px;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Add mobile-friendly styles
const mobileCardStyles = css`
  @media (max-width: 768px) {
    .ant-table {
      display: none;
    }
    .mobile-cards {
      display: block;
    }
    .ant-space {
      flex-wrap: wrap;
      gap: 8px !important;
    }
    .ant-space-item {
      flex: 1 1 calc(50% - 4px);
      min-width: calc(50% - 4px);
    }
    .ant-input-search {
      width: 100% !important;
    }
  }
  @media (min-width: 769px) {
    .mobile-cards {
      display: none;
    }
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  cursor: pointer;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  margin-bottom: 16px;

  .ant-space {
    display: flex;
    gap: 8px;
  }

  @media (min-width: 769px) {
    .ant-space {
      flex-direction: row;
      width: auto;
    }

    .ant-space-item {
      width: auto;
    }

    .ant-btn {
      width: auto;
    }

    .ant-upload {
      width: auto;
    }

    .ant-upload button {
      width: auto;
    }
  }

  @media (max-width: 768px) {
    > div {
      flex-direction: column;
    }

    .ant-space {
      width: 100%;
      margin-top: 16px;
    }

    .ant-space-item {
      width: 100%;
    }

    .ant-btn {
      width: 100%;
    }

    .ant-upload {
      width: 100%;
    }

    .ant-upload button {
      width: 100%;
    }
  }
`;

// Filter products based on search text
const filterProducts = (products: CompanyProduct[], searchText: string) => {
  if (!Array.isArray(products)) return [];

  // More thorough validation and filtering of products
  const validProducts = products.filter(
    (product): product is CompanyProduct =>
      product !== null &&
      product !== undefined &&
      typeof product === "object" &&
      // Ensure product has required properties
      "id" in product
  );

  if (!searchText) return validProducts;

  const searchTermLower = searchText.toLowerCase();
  return validProducts.filter((product) => {
    try {
      return (
        (product.name &&
          product.name.toLowerCase().includes(searchTermLower)) ||
        (product.ean && product.ean.toString().includes(searchTermLower)) ||
        (product.hs_code &&
          product.hs_code.toString().includes(searchTermLower))
      );
    } catch (error) {
      console.error("Error filtering product:", error, product);
      return false;
    }
  });
};

// Contact info component to ensure consistent rendering
const ContactInfo = () => {
  const { t } = useTranslation();
  const email = "info@efreightforwarding.com";
  const phone = "+31 (0)85 0607 063";

  // Split the translation string into parts
  const parts = t("companyInfo.contactInfo", {
    email: "{email}",
    phone: "{phone}",
  }).split(/\{(email|phone)\}/);

  return (
    <span>
      {parts.map((part, index) => {
        if (part === "email") {
          return (
            <a key={index} href={`mailto:${email}`}>
              {email}
            </a>
          );
        } else if (part === "phone") {
          return (
            <a key={index} href={`tel:${phone.replace(/\s/g, "")}`}>
              {phone}
            </a>
          );
        } else {
          return <span key={index}>{part}</span>;
        }
      })}
    </span>
  );
};

// Function to check if a product has bookings
const useProductBookingsInfo = (
  ean: number | null,
  showLiveOnly: boolean = false
) => {
  const { bookings, loading, error } = useProductBookings(ean, showLiveOnly);
  return {
    bookings: bookings && Array.isArray(bookings) ? bookings : [],
    bookingsCount: bookings && Array.isArray(bookings) ? bookings.length : 0,
    loading,
    error,
  };
};

// Booking links component with accordion
const BookingLinks: React.FC<{ ean: number | null; showLiveOnly: boolean }> = ({
  ean,
  showLiveOnly,
}) => {
  const { t } = useTranslation();
  const { bookings, loading, error } = useProductBookingsInfo(
    ean,
    showLiveOnly
  );
  const [expanded, setExpanded] = useState(false);

  if (loading) return <span>...</span>;
  if (error)
    return <Typography.Text type="danger">{t("common.error")}</Typography.Text>;
  if (!bookings.length)
    return <Typography.Text type="secondary">-</Typography.Text>;

  // Format booking ID with transport abbreviation
  const formatBookingId = (booking: {
    id: number;
    transport_abbreviation?: string;
  }) => {
    const prefix = "EFF";
    const transportSuffix = booking.transport_abbreviation || "AIR";
    return `${prefix}${booking.id}${transportSuffix}`;
  };

  // For single booking, just show it directly
  if (bookings.length === 1) {
    return (
      <Typography.Link
        onClick={() =>
          window.open(`/bookings/${bookings[0].id}/view`, "_blank")
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
        }}
      >
        <span
          style={{
            marginRight: "8px",
            fontWeight: "bold",
            color: "#1890ff",
            whiteSpace: "nowrap",
          }}
        >
          #{formatBookingId(bookings[0])}
        </span>
        <Tag style={{ marginLeft: 0, whiteSpace: "nowrap" }}>
          {(() => {
            const status = bookings[0].status;
            const normalizedStatus = status.replace(/-/g, "_");
            const translation = t(`status.${normalizedStatus}`);
            return typeof translation === "string"
              ? translation
              : normalizedStatus;
          })()}
        </Tag>
      </Typography.Link>
    );
  }

  // For multiple bookings, create a collapsible section
  return (
    <div>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          fontWeight: "bold",
          color: "#1890ff",
          userSelect: "none",
        }}
      >
        <CaretRightOutlined
          style={{
            marginRight: 8,
            fontSize: 12,
            transform: expanded ? "rotate(90deg)" : "none",
            transition: "transform 0.2s",
          }}
        />
        {bookings.length} {t("myProducts.bookings")}
      </div>

      {expanded && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginTop: 8,
            marginLeft: 16,
            borderLeft: "1px solid #f0f0f0",
            paddingLeft: 12,
          }}
        >
          {bookings.map((booking) => (
            <div
              key={booking.id}
              style={{
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <Typography.Link
                onClick={() =>
                  window.open(`/bookings/${booking.id}/view`, "_blank")
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <span
                  style={{
                    marginRight: "8px",
                    fontWeight: "bold",
                    color: "#1890ff",
                    whiteSpace: "nowrap",
                  }}
                >
                  #{formatBookingId(booking)}
                </span>
                <Tag style={{ marginLeft: 0, whiteSpace: "nowrap" }}>
                  {(() => {
                    const status = booking.status;
                    const normalizedStatus = status.replace(/-/g, "_");
                    const translation = t(`status.${normalizedStatus}`);
                    return typeof translation === "string"
                      ? translation
                      : normalizedStatus;
                  })()}
                </Tag>
              </Typography.Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MyProducts: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { selectedCompany } = useCompany();
  const { details, loading: companyLoading } =
    useCompanyDetails(selectedCompany);
  const {
    products,
    loading,
    saveCompanyProduct,
    updateCompanyProduct,
    deleteCompanyProduct,
  } = useCompanyProducts(selectedCompany);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<CompanyProduct | null>(
    null
  );
  const [searchText, setSearchText] = useState("");
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [showLiveBookingsOnly, setShowLiveBookingsOnly] = useState(true);
  const [stockData, setStockData] = useState<Map<number, number>>(new Map());
  const [loadingStock, setLoadingStock] = useState(false);

  // Check if company is approved
  const isCompanyApproved = details?.status === "approved";

  // Check if Picqer is connected for this company
  const isPicqerConnected =
    details?.picqer_enabled && details?.picqer_fulfilment_customer_id;

  // Fetch stock data for Picqer-linked products
  const fetchStockData = async () => {
    if (!selectedCompany || !isPicqerConnected) return;

    setLoadingStock(true);
    try {
      const result = await PicqerService.getProductStock(selectedCompany);
      if (result.success && result.data) {
        const stockMap = new Map<number, number>();
        result.data.forEach((item) => {
          stockMap.set(item.productId, item.stock);
        });
        setStockData(stockMap);
      } else {
        console.error("Failed to fetch stock data:", result.error);
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
    } finally {
      setLoadingStock(false);
    }
  };

  // Fetch stock data when component loads or when products change
  useEffect(() => {
    if (isPicqerConnected && products.length > 0) {
      fetchStockData();
    }
  }, [selectedCompany, isPicqerConnected, products.length]);

  useEffect(() => {
    const editProductId = location.state?.editProductId;
    if (editProductId && products.length > 0) {
      const productToEdit = products.find((p) => p.id === editProductId);
      if (productToEdit) {
        handleEdit(productToEdit);
      }
    }
  }, [location.state, products]);

  const handleAdd = () => {
    form.resetFields();
    setEditingProduct(null);
    setIsModalVisible(true);
    // Hide navigation modal if it's open
    setShowNavigationModal(false);
  };

  const handleEdit = (record: CompanyProduct) => {
    form.setFieldsValue(record);
    setEditingProduct(record);
    setIsModalVisible(true);
    // Hide navigation modal if it's open
    setShowNavigationModal(false);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCompanyProduct(id);
      message.success(t("myProducts.productDeletedSuccess"));
    } catch {
      message.error(t("myProducts.deleteError"));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const productData = {
        ...values,
        company_id: selectedCompany!,
        pieces_per_carton: values.pieces_per_carton || null,
        weight_per_carton: values.weight_per_carton || null,
        carton_length: values.carton_length || null,
        carton_width: values.carton_width || null,
        carton_height: values.carton_height || null,
        unit_price: values.unit_price || null,
      };

      if (editingProduct) {
        await updateCompanyProduct(editingProduct.id, productData);
        message.success(t("myProducts.productUpdatedSuccess"));
      } else {
        await saveCompanyProduct(productData);
        message.success(t("myProducts.productAddedSuccess"));
      }

      // Close the edit modal first before showing the navigation modal
      setIsModalVisible(false);
      form.resetFields();

      // If we came from a booking, show the navigation modal
      if (location.state?.editProductId) {
        // Add a small delay before showing the navigation modal
        setTimeout(() => {
          setShowNavigationModal(true);
        }, 100);
      } else {
        setEditingProduct(null);
        navigate(".", { replace: true });
      }
    } catch {
      message.error(t("myProducts.saveError"));
    }
  };

  const handleNavigationChoice = (returnToBooking: boolean) => {
    // Make sure we close all modals
    setShowNavigationModal(false);
    setIsModalVisible(false);
    setEditingProduct(null);

    if (returnToBooking) {
      // Get the data we need to preserve from location state
      const productId = location.state?.editProductId;
      const product = products.find((p) => p.id === productId);
      const currentStep = location.state?.currentStep || 2; // Default to product step (index 2)
      const formState = location.state?.formState || {}; // Get preserved form values
      const bookingId = location.state?.bookingId;
      const previousProducts = location.state?.previousProducts || [];

      // If there's a booking ID, navigate to the edit URL instead of going back
      if (bookingId) {
        navigate(`/bookings/${bookingId}/edit`, {
          state: {
            updatedProduct: product,
            fromProductPage: true,
            returnToStep: currentStep,
            preservedFormState: formState,
            previousProducts: previousProducts,
            forceStep: currentStep, // Add an additional flag to force the step
          },
          replace: true, // Use replace to avoid history stack issues
        });
      } else {
        // Fall back to the previous behavior if no booking ID
        navigate(-1 as unknown as string, {
          state: {
            updatedProduct: product,
            fromProductPage: true,
            returnToStep: currentStep,
            preservedFormState: formState,
            previousProducts: previousProducts,
            forceStep: currentStep, // Add an additional flag to force the step
          },
        });
      }
    } else {
      navigate(".", { replace: true }); // Stay on My Products page
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = [
      "name,ean,hs_code,pieces_per_carton,weight_per_carton,carton_length,carton_width,carton_height,unit_price",
      "Example Product,1234567890123,1234,12,10.5,30,20,15,25.99",
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "product_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get filtered products
  const filteredProducts = filterProducts(products, searchText);

  // Build columns array conditionally based on Picqer connection
  const buildColumns = (): ColumnsType<CompanyProduct> => {
    const baseColumns: ColumnsType<CompanyProduct> = [
      {
        title: t("products.name"),
        dataIndex: "name",
        key: "name",
        sorter: (a, b) => (a?.name || "").localeCompare(b?.name || ""),
        filteredValue: [searchText],
        onFilter: (value, record) =>
          (record?.name?.toLowerCase() || "").includes(
            value.toString().toLowerCase()
          ) ||
          (record?.hs_code?.toString() || "").includes(value.toString()) ||
          (record?.ean?.toString() || "").includes(value.toString()),
      },
      {
        title: t("products.ean"),
        dataIndex: "ean",
        key: "ean",
        sorter: (a, b) => (a?.ean || 0) - (b?.ean || 0),
      },
    ];

    // Add Picqer columns only if connected
    if (isPicqerConnected) {
      baseColumns.push(
        {
          title: t("myProducts.picqerProductId", "Picqer ID"),
          dataIndex: "picqer_product_id",
          key: "picqer_product_id",
          width: 120,
          sorter: (a, b) =>
            (a?.picqer_product_id || 0) - (b?.picqer_product_id || 0),
          render: (value: number | null) => {
            if (value) {
              return (
                <Tag
                  color="blue"
                  style={{ fontSize: "11px", cursor: "pointer" }}
                  onClick={() => {
                    window.open(
                      `https://e-freight-forwarding.picqer.com/fulfilmentportal/products/${value}`,
                      "_blank"
                    );
                  }}
                >
                  #{value}
                </Tag>
              );
            }
            return <span style={{ color: "#999", fontSize: "11px" }}>-</span>;
          },
        },
        {
          title: (
            <Space>
              {t("myProducts.picqerStock", "Picqer Stock")}
              <Tooltip
                title={t(
                  "myProducts.picqerStockTooltip",
                  "Real-time stock from Picqer inventory system"
                )}
              >
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", fontSize: "12px" }}
                />
              </Tooltip>
            </Space>
          ),
          key: "picqer_stock",
          width: 140,
          render: (_: unknown, record: CompanyProduct) => {
            if (!record.picqer_product_id) {
              return <span style={{ color: "#999", fontSize: "11px" }}>-</span>;
            }

            const stock = stockData.get(record.id);
            if (loadingStock) {
              return (
                <Space>
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      border: "2px solid #f3f3f3",
                      borderTop: "2px solid #1890ff",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  <span style={{ fontSize: "11px", color: "#8c8c8c" }}>
                    Loading...
                  </span>
                </Space>
              );
            }

            if (stock !== undefined) {
              return (
                <Space>
                  <Tag
                    color={stock > 0 ? "green" : stock === 0 ? "orange" : "red"}
                    style={{ fontSize: "11px", margin: 0 }}
                  >
                    {stock} pcs
                  </Tag>
                  <span style={{ fontSize: "10px", color: "#8c8c8c" }}>
                    Picqer
                  </span>
                </Space>
              );
            }

            return <span style={{ color: "#999", fontSize: "11px" }}>-</span>;
          },
        }
      );
    }

    // Add remaining columns
    baseColumns.push(
      {
        title: t("products.hsCode"),
        dataIndex: "hs_code",
        key: "hs_code",
        sorter: (a, b) => (a?.hs_code || 0) - (b?.hs_code || 0),
      },
      {
        title: t("myProducts.piecesPerCarton"),
        dataIndex: "pieces_per_carton",
        key: "pieces_per_carton",
        sorter: (a, b) =>
          (a?.pieces_per_carton || 0) - (b?.pieces_per_carton || 0),
      },
      {
        title: t("myProducts.weightPerCarton"),
        dataIndex: "weight_per_carton",
        key: "weight_per_carton",
        sorter: (a, b) =>
          (a?.weight_per_carton || 0) - (b?.weight_per_carton || 0),
      },
      {
        title: t("myProducts.dimensions"),
        key: "dimensions",
        render: (_: unknown, record: CompanyProduct) =>
          `${record?.carton_length || "-"} × ${record?.carton_width || "-"} × ${
            record?.carton_height || "-"
          }`,
      },
      {
        title: t("myProducts.unitPrice"),
        dataIndex: "unit_price",
        key: "unit_price",
        sorter: (a, b) => (a?.unit_price || 0) - (b?.unit_price || 0),
        render: (value) => (value ? `$${value}` : "-"),
      },
      {
        title: t("myProducts.bookings", "Bookings"),
        key: "bookings",
        width: 200,
        render: (_, record) => {
          if (!record || record.ean === null || record.ean === undefined)
            return null;
          try {
            return (
              <BookingLinks
                ean={record.ean}
                showLiveOnly={showLiveBookingsOnly}
              />
            );
          } catch (error) {
            console.error("Error rendering BookingLinks:", error);
            return null;
          }
        },
      },
      {
        title: t("common.actions"),
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
            <Popconfirm
              title={t("myProducts.deleteConfirmTitle")}
              description={t("myProducts.deleteConfirmDescription")}
              onConfirm={() => handleDelete(record.id)}
              okText={t("common.yes")}
              cancelText={t("common.no")}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      }
    );

    return baseColumns;
  };

  const columns = buildColumns();

  const handleCsvImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        const lines = csv.split("\n");
        const products = lines
          .slice(1)
          .filter((line) => line.trim())
          .map((line) => {
            const values = line.split(",");
            return {
              name: values[0],
              ean: values[1] ? Number(values[1]) : null,
              hs_code: values[2] ? Number(values[2]) : null,
              pieces_per_carton: values[3] ? Number(values[3]) : null,
              weight_per_carton: values[4] ? Number(values[4]) : null,
              carton_length: values[5] ? Number(values[5]) : null,
              carton_width: values[6] ? Number(values[6]) : null,
              carton_height: values[7] ? Number(values[7]) : null,
              unit_price: values[8] ? Number(values[8]) : null,
              company_id: selectedCompany!,
            };
          });

        Promise.all(products.map((product) => saveCompanyProduct(product)))
          .then(() => {
            message.success(
              t("myProducts.importSuccess", {
                count: products.length,
              })
            );
          })
          .catch((error) => {
            message.error(t("myProducts.importError"));
            console.error("Import error:", error);
          });
      } catch (error) {
        message.error(t("myProducts.parseError"));
        console.error("Parse error:", error);
      }
    };
    reader.readAsText(file);
    return false;
  };

  return (
    <div className={mobileCardStyles}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Typography.Title level={2}>{t("myProducts.title")}</Typography.Title>
      </div>

      {/* Add alerts for company verification status */}
      {!companyLoading && details?.status === "under_review" && (
        <Alert
          type="info"
          message={t("bookings.companyUnderReview")}
          style={{ marginBottom: 16 }}
        />
      )}

      {!companyLoading && details?.status === "pending" && (
        <Alert
          type="warning"
          message={
            <span>
              {t("companyInfo.verificationInProgress")}
              <br />
              <br />
              <ContactInfo />
            </span>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {!companyLoading &&
        !isCompanyApproved &&
        details?.status !== "under_review" &&
        details?.status !== "pending" && (
          <Alert
            type="warning"
            message={
              <span>
                {t(
                  "myProducts.completeCompanyInfoPrompt",
                  "You need to complete your company information before you can create products. Products are connected to a company, so you need to have a verified company first."
                )}
                <br />
                <a onClick={() => navigate("/company-info")}>
                  {t("common.companyInfo")}
                </a>
              </span>
            }
            style={{ marginBottom: 16 }}
          />
        )}

      <Collapse defaultActiveKey={[]} style={{ marginBottom: 16 }}>
        <Panel header={t("myProducts.importInstructions.title")} key="1">
          <Typography.Paragraph>
            {t("myProducts.importInstructions.notes")}
          </Typography.Paragraph>

          <List
            size="small"
            bordered
            dataSource={[
              t("myProducts.importInstructions.step1"),
              t("myProducts.importInstructions.step2"),
              t("myProducts.importInstructions.step3"),
              t("myProducts.importInstructions.step4"),
              t("myProducts.importInstructions.step5"),
            ]}
            renderItem={(item, index) => (
              <List.Item>{`${index + 1}. ${item}`}</List.Item>
            )}
          />
        </Panel>

        <Panel
          header={t("myProducts.importInstructions.requiredFields")}
          key="2"
        >
          <Table
            size="small"
            pagination={false}
            dataSource={[
              {
                key: "name",
                field: "name",
                label: t("myProducts.productName"),
                requirement: t("myProducts.enterProductName"),
              },
              {
                key: "ean",
                field: "ean",
                label: t("myProducts.ean"),
                requirement: t("myProducts.eanRequired"),
              },
              {
                key: "hs_code",
                field: "hs_code",
                label: t("myProducts.hsCode"),
                requirement: t("myProducts.hsCodeRequired"),
              },
              {
                key: "pieces_per_carton",
                field: "pieces_per_carton",
                label: t("myProducts.piecesPerCarton"),
                requirement: t("myProducts.piecesPerCartonRequired"),
              },
              {
                key: "weight_per_carton",
                field: "weight_per_carton",
                label: t("myProducts.weightPerCarton"),
                requirement: t("myProducts.weightPerCartonRequired"),
              },
              {
                key: "carton_length",
                field: "carton_length",
                label: t("myProducts.cartonDimensions"),
                requirement: t("myProducts.lengthRequired"),
              },
              {
                key: "carton_width",
                field: "carton_width",
                label: t("myProducts.cartonDimensions"),
                requirement: t("myProducts.widthRequired"),
              },
              {
                key: "carton_height",
                field: "carton_height",
                label: t("myProducts.cartonDimensions"),
                requirement: t("myProducts.heightRequired"),
              },
              {
                key: "unit_price",
                field: "unit_price",
                label: t("myProducts.unitPrice"),
                requirement: t("myProducts.unitPriceRequired"),
              },
            ]}
            columns={[
              {
                title: "CSV Column Name",
                dataIndex: "field",
                key: "field",
                render: (text) => (
                  <Typography.Text code>{text}</Typography.Text>
                ),
              },
              {
                title: "Description",
                dataIndex: "label",
                key: "label",
                render: (text) => (
                  <Typography.Text strong>{text}</Typography.Text>
                ),
              },
              {
                title: "Requirements",
                dataIndex: "requirement",
                key: "requirement",
                render: (text) => (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: "12px" }}
                  >
                    {text}
                  </Typography.Text>
                ),
              },
            ]}
            className={compactTableStyles}
          />
        </Panel>
      </Collapse>

      <ButtonContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div>
            <Search
              placeholder={t("myProducts.searchPlaceholder")}
              onSearch={(value) => setSearchText(value)}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />
          </div>
          <Space size="middle">
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              {t("myProducts.downloadTemplate")}
            </Button>
            <Upload
              name="file"
              beforeUpload={handleCsvImport}
              accept=".csv"
              fileList={[]}
              disabled={
                !isCompanyApproved ||
                companyLoading ||
                details?.status === "under_review"
              }
            >
              <Button
                icon={<UploadOutlined />}
                disabled={
                  !isCompanyApproved ||
                  companyLoading ||
                  details?.status === "under_review"
                }
              >
                {t("myProducts.importProducts")}
              </Button>
            </Upload>
            {isPicqerConnected && (
              <Button
                icon={<InfoCircleOutlined />}
                onClick={fetchStockData}
                loading={loadingStock}
                disabled={
                  !isCompanyApproved ||
                  companyLoading ||
                  details?.status === "under_review"
                }
                title={t(
                  "myProducts.refreshStock",
                  "Refresh Picqer stock data"
                )}
              >
                {t("myProducts.refreshStock", "Refresh Stock")}
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={
                !isCompanyApproved ||
                companyLoading ||
                details?.status === "under_review"
              }
            >
              {t("myProducts.addProduct")}
            </Button>
          </Space>
        </div>
      </ButtonContainer>

      <div
        style={{
          display: "flex",
          marginBottom: 16,
          justifyContent: "flex-end",
        }}
      >
        <Tooltip
          title={t(
            "myProducts.liveBookingsTooltip",
            "Shows only bookings with status: Concept, Submitted, Approved, Rejected, Waiting for Goods, Goods Received at Origin, In Transit, Custom Clearance, and Out for Delivery."
          )}
        >
          <Checkbox
            checked={showLiveBookingsOnly}
            onChange={(e) => setShowLiveBookingsOnly(e.target.checked)}
          >
            {t("myProducts.showLiveBookingsOnly", "Show live bookings only")}
          </Checkbox>
        </Tooltip>
      </div>

      <Table
        columns={columns}
        dataSource={
          Array.isArray(filteredProducts)
            ? filteredProducts.filter(Boolean)
            : []
        }
        loading={loading}
        rowKey={(record) => record?.id?.toString() || Math.random().toString()}
        pagination={{ pageSize: 20 }}
        size="small"
        className={compactTableStyles}
      />

      <div className="mobile-cards">
        <div style={{ marginBottom: 16 }}>
          <Tooltip
            title={t(
              "myProducts.liveBookingsTooltip",
              "Shows only bookings with status: Concept, Submitted, Approved, Rejected, Waiting for Goods, Goods Received at Origin, In Transit, Custom Clearance, and Out for Delivery."
            )}
          >
            <Checkbox
              checked={showLiveBookingsOnly}
              onChange={(e) => setShowLiveBookingsOnly(e.target.checked)}
            >
              {t("myProducts.showLiveBookingsOnly", "Show live bookings only")}
            </Checkbox>
          </Tooltip>
        </div>

        {products.map((product) => (
          <ProductCard key={product.id}>
            <CardHeader>
              <span style={{ fontWeight: "bold" }}>{product.name}</span>
              <Space>
                <Button
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(product);
                  }}
                />
                <Popconfirm
                  title={t("myProducts.deleteConfirmTitle")}
                  description={t("myProducts.deleteConfirmDescription")}
                  onConfirm={() => handleDelete(product.id)}
                  okText={t("common.yes")}
                  cancelText={t("common.no")}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </Space>
            </CardHeader>
            <CardContent>
              <div>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                  {t("products.ean")}:
                </span>{" "}
                {product.ean || "-"}
              </div>
              <div>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                  {t("myProducts.picqerProductId")}:
                </span>{" "}
                {product.picqer_product_id
                  ? `#${product.picqer_product_id}`
                  : "-"}
              </div>
              <div>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                  {t("products.hsCode")}:
                </span>{" "}
                {product.hs_code || "-"}
              </div>
              <div>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                  {t("myProducts.piecesPerCarton")}:
                </span>{" "}
                {product.pieces_per_carton || "-"}
              </div>
              <div>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                  {t("myProducts.weightPerCarton")}:
                </span>{" "}
                {product.weight_per_carton || "-"}
              </div>
              <div>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                  {t("myProducts.dimensions")}:
                </span>{" "}
                {`${product.carton_length || "-"} × ${
                  product.carton_width || "-"
                } × ${product.carton_height || "-"}`}
              </div>
              <div>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                  {t("myProducts.unitPrice")}:
                </span>{" "}
                {product.unit_price ? `$${product.unit_price}` : "-"}
              </div>
              {product.ean && (
                <div>
                  <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                    {t("myProducts.bookings")}:
                  </span>{" "}
                  <BookingLinks
                    ean={product.ean}
                    showLiveOnly={showLiveBookingsOnly}
                  />
                </div>
              )}
            </CardContent>
          </ProductCard>
        ))}
      </div>

      <Modal
        title={
          editingProduct
            ? t("myProducts.editProduct")
            : t("myProducts.addProduct")
        }
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingProduct(null);
        }}
        destroyOnClose={true}
        maskClosable={false}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            pieces_per_carton: null,
            weight_per_carton: null,
            carton_length: null,
            carton_width: null,
            carton_height: null,
            unit_price: null,
          }}
        >
          <Form.Item
            name="name"
            label={t("myProducts.productName")}
            rules={[
              { required: true, message: t("myProducts.enterProductName") },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="ean"
            label={
              <Space>
                {t("myProducts.ean")}
                <Tooltip title={t("myProducts.eanTooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            rules={[
              {
                required: true,
                message: t("myProducts.eanRequired") || "Please enter EAN",
              },
              {
                validator: (_, value) => {
                  if (value && value.toString().length > 13) {
                    return Promise.reject(
                      new Error("EAN must be maximum 13 digits")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} maxLength={13} />
          </Form.Item>

          <Form.Item
            name="hs_code"
            label={
              <Space>
                {t("myProducts.hsCode")}
                <Tooltip title={t("myProducts.hsCodeTooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            rules={[
              {
                required: true,
                message:
                  t("myProducts.hsCodeRequired") || "Please enter HS Code",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="pieces_per_carton"
            label={t("myProducts.piecesPerCarton")}
            rules={[
              {
                required: true,
                message:
                  t("myProducts.piecesPerCartonRequired") ||
                  "Please enter pieces per carton",
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="weight_per_carton"
            label={t("myProducts.weightPerCarton")}
            rules={[
              {
                required: true,
                message:
                  t("myProducts.weightPerCartonRequired") ||
                  "Please enter weight per carton",
              },
            ]}
          >
            <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label={t("myProducts.cartonDimensions")}
            required
            tooltip={
              t("myProducts.dimensionsRequired") ||
              "All dimensions are required"
            }
          >
            <Space>
              <Form.Item
                name="carton_length"
                noStyle
                rules={[
                  {
                    required: true,
                    message: t("myProducts.lengthRequired") || "Required",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.1}
                  placeholder={t("myProducts.length")}
                />
              </Form.Item>
              <Form.Item
                name="carton_width"
                noStyle
                rules={[
                  {
                    required: true,
                    message: t("myProducts.widthRequired") || "Required",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.1}
                  placeholder={t("myProducts.width")}
                />
              </Form.Item>
              <Form.Item
                name="carton_height"
                noStyle
                rules={[
                  {
                    required: true,
                    message: t("myProducts.heightRequired") || "Required",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.1}
                  placeholder={t("myProducts.height")}
                />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item
            name="unit_price"
            label={t("myProducts.unitPrice")}
            rules={[
              {
                required: true,
                message:
                  t("myProducts.unitPriceRequired") ||
                  "Please enter unit price",
              },
            ]}
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              addonBefore="$"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("myProducts.productSaved")}
        open={showNavigationModal}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        style={{ zIndex: 1050 }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text style={{ fontSize: 16 }}>
            {t("myProducts.productSavedMessage")}
          </Typography.Text>
          <Space
            style={{
              width: "100%",
              justifyContent: "flex-end",
              marginTop: 24,
            }}
          >
            <Button onClick={() => handleNavigationChoice(false)}>
              {t("myProducts.stayOnProducts")}
            </Button>
            <Button type="primary" onClick={() => handleNavigationChoice(true)}>
              {t("myProducts.returnToBooking")}
            </Button>
          </Space>
        </Space>
      </Modal>
    </div>
  );
};

export default MyProducts;
